// HKDSE 2024 Paper 1 Knowledge Graph Samples - Complete Set
// Organized by question types with systematic naming

export const hkdse2024Samples_complete = {
  algebra: [
    {
      id: 'hkdse2024_algebra_01',
      problem: "化簡 $\\frac{2}{4h-7} - \\frac{3}{6h-5}$。",
      problemType: "algebra_fractions",
      difficulty: "intermediate",
      description: "Simplifying algebraic fractions",
      graphData: {
        nodes: [
          {
            id: "fraction_subtraction",
            name: "Fraction Subtraction",
            type: "concepts",
            description: "Subtracting two algebraic fractions",
            value: "2/(4h-7) - 3/(6h-5)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "common_denominator",
            name: "Common Denominator",
            type: "methods",
            description: "Finding common denominator",
            value: "LCD = (4h-7)(6h-5)",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "numerator_expansion",
            name: "Numerator Expansion",
            type: "methods",
            description: "Expanding numerators",
            value: "2(6h-5) - 3(4h-7)",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "simplified_form",
            name: "Simplified Form",
            type: "concepts",
            description: "Final simplified expression",
            value: "Simplified result",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "fraction_subtraction",
            to: "common_denominator",
            type: "requires",
            strength: 9
          },
          {
            from: "common_denominator",
            to: "numerator_expansion",
            type: "enables",
            strength: 9
          },
          {
            from: "numerator_expansion",
            to: "simplified_form",
            type: "results_in",
            strength: 8
          }
        ]
      }
    },
    {
      id: 'hkdse2024_algebra_02',
      problem: "令 $x$ 成為公式 $\\frac{Ax+C}{B} = 3x$ 的主項。",
      problemType: "algebra_formula",
      difficulty: "intermediate",
      description: "Rearranging formula to make x the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fraction",
            value: "(Ax+C)/B = 3x",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "methods",
            description: "Multiplying both sides by B",
            value: "Ax + C = 3Bx",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "collecting_terms",
            name: "Collecting Terms",
            type: "methods",
            description: "Collecting x terms on one side",
            value: "Ax - 3Bx = -C",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "subject_formula",
            name: "Subject of Formula",
            type: "concepts",
            description: "Making x the subject",
            value: "x = -C/(A-3B)",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "fraction_equation",
            to: "cross_multiplication",
            type: "solved_by",
            strength: 9
          },
          {
            from: "cross_multiplication",
            to: "collecting_terms",
            type: "leads_to",
            strength: 9
          },
          {
            from: "collecting_terms",
            to: "subject_formula",
            type: "results_in",
            strength: 8
          }
        ]
      }
    }
  ],
  factorization: [
    {
      id: 'hkdse2024_factorization_01',
      problem: "因式分解 (a) $6r^2 - 13rs - 28s^2$，(b) $4r - 14s + 6r^2 - 13rs - 28s^2$。",
      problemType: "factorization_quadratic",
      difficulty: "intermediate",
      description: "Factorizing quadratic expressions in two variables",
      graphData: {
        nodes: [
          {
            id: "quadratic_expression",
            name: "Quadratic Expression",
            type: "concepts",
            description: "6r² - 13rs - 28s²",
            value: "6r² - 13rs - 28s²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "factoring_method",
            name: "Factoring Method",
            type: "methods",
            description: "Using trial and error or grouping",
            value: "Find factors of 6 and -28",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "factored_form_a",
            name: "Factored Form (a)",
            type: "concepts",
            description: "Result for part (a)",
            value: "(2r-7s)(3r+4s)",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "factored_form_b",
            name: "Factored Form (b)",
            type: "concepts",
            description: "Result for part (b)",
            value: "2(2r-7s) + (2r-7s)(3r+4s)",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "quadratic_expression",
            to: "factoring_method",
            type: "solved_by",
            strength: 9
          },
          {
            from: "factoring_method",
            to: "factored_form_a",
            type: "provides",
            strength: 9
          },
          {
            from: "factored_form_a",
            to: "factored_form_b",
            type: "used_in",
            strength: 8
          }
        ]
      }
    }
  ],
  inequalities: [
    {
      id: 'hkdse2024_inequalities_01',
      problem: "(a) 求同時滿足 $\\frac{5x+7}{4} - 1 < 2x$ 及 $3x + 9 \\geq 0$ 的 $x$ 值的範圍。(b) 寫出同時滿足 (a) 的不等式的最小整數。",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities with 'and'",
      graphData: {
        nodes: [
          {
            id: "first_inequality",
            name: "First Inequality",
            type: "concepts",
            description: "(5x+7)/4 - 1 < 2x",
            value: "(5x+7)/4 - 1 < 2x",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "second_inequality",
            name: "Second Inequality",
            type: "concepts",
            description: "3x + 9 ≥ 0",
            value: "3x + 9 ≥ 0",
            importance: 8,
            x: 200, y: -200, z: 0
          },
          {
            id: "compound_inequality",
            name: "Compound Inequality",
            type: "concepts",
            description: "Inequality with 'and' condition",
            value: "First AND Second",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "smallest_integer",
            name: "Smallest Integer",
            type: "concepts",
            description: "Smallest integer in solution set",
            value: "Find smallest integer satisfying both",
            importance: 7,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "first_inequality",
            to: "compound_inequality",
            type: "part_of",
            strength: 9
          },
          {
            from: "second_inequality",
            to: "compound_inequality",
            type: "part_of",
            strength: 8
          },
          {
            from: "compound_inequality",
            to: "smallest_integer",
            type: "constrains",
            strength: 7
          }
        ]
      }
    }
  ],
  ratios: [
    {
      id: 'hkdse2024_ratios_01',
      problem: "設 $a$、$b$ 及 $c$ 均為非零的數使得 $5a = 6c$ 及 $\\frac{2b+7c}{b+c} = 4$。求 $\\frac{5a+8b}{2b+3c}$。",
      problemType: "ratios_proportion",
      difficulty: "intermediate",
      description: "Working with ratios and proportions",
      graphData: {
        nodes: [
          {
            id: "ratio_ac",
            name: "Ratio a:c",
            type: "concepts",
            description: "Ratio of a to c",
            value: "5a = 6c",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "proportion_equation",
            name: "Proportion Equation",
            type: "concepts",
            description: "Proportion involving b and c",
            value: "(2b+7c)/(b+c) = 4",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "variable_relationships",
            name: "Variable Relationships",
            type: "methods",
            description: "Expressing variables in terms of one variable",
            value: "a = 6k, c = 5k, b = ...",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "target_expression",
            name: "Target Expression",
            type: "concepts",
            description: "Expression to be evaluated",
            value: "(5a+8b)/(2b+3c)",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "ratio_ac",
            to: "variable_relationships",
            type: "provides_constraint",
            strength: 9
          },
          {
            from: "proportion_equation",
            to: "variable_relationships",
            type: "provides_constraint",
            strength: 9
          },
          {
            from: "variable_relationships",
            to: "target_expression",
            type: "evaluates",
            strength: 8
          }
        ]
      }
    }
  ],
  word_problems: [
    {
      id: 'hkdse2024_word_problems_01',
      problem: "某計算機的標價較其成本高 $40\\%$。該計算機以其標價七五折售出並獲利 $\\$13$。求該計算機的標價。",
      problemType: "word_problems_percentage_profit",
      difficulty: "intermediate",
      description: "Solving percentage and profit problems",
      graphData: {
        nodes: [
          {
            id: "markup_relationship",
            name: "Markup Relationship",
            type: "concepts",
            description: "Selling price 40% higher than cost",
            value: "Selling price = Cost × 1.4",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "discount_sale",
            name: "Discount Sale",
            type: "concepts",
            description: "Sold at 75% of selling price",
            value: "Actual sale = Selling price × 0.75",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "profit_given",
            name: "Profit Given",
            type: "concepts",
            description: "Profit is $13",
            value: "Actual sale - Cost = $13",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "selling_price",
            name: "Selling Price",
            type: "variables",
            description: "Selling price to find",
            value: "Selling price",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "markup_relationship",
            to: "profit_given",
            type: "provides_equation",
            strength: 9
          },
          {
            from: "discount_sale",
            to: "profit_given",
            type: "provides_equation",
            strength: 9
          },
          {
            from: "profit_given",
            to: "selling_price",
            type: "solves_for",
            strength: 10
          }
        ]
      }
    }
  ],
  polar_coordinates: [
    {
      id: 'hkdse2024_polar_coordinates_01',
      problem: "在某極坐標系中，$O$ 為極點。點 $P$、點 $Q$ 及點 $R$ 的極坐標分別為 $(11, 59°)$、$(60, 149°)$ 及 $(144, 239°)$。(a) 求 $\\angle POQ$。(b) $P$、$O$ 與 $R$ 是否共線？試解釋你的答案。(c) 求 $\\triangle PQR$ 的周界。",
      problemType: "polar_coordinates_geometry",
      difficulty: "intermediate",
      description: "Working with polar coordinates and geometry",
      graphData: {
        nodes: [
          {
            id: "polar_coordinates",
            name: "Polar Coordinates",
            type: "concepts",
            description: "Points in polar form (r, θ)",
            value: "P(11,59°), Q(60,149°), R(144,239°)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "angle_calculation",
            name: "Angle Calculation",
            type: "methods",
            description: "Calculating angle between points",
            value: "∠POQ = |149° - 59°| = 90°",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "collinearity_check",
            name: "Collinearity Check",
            type: "methods",
            description: "Checking if points are collinear",
            value: "Check if P, O, R are on same line",
            importance: 8,
            x: 200, y: 0, z: 0
          },
          {
            id: "triangle_perimeter",
            name: "Triangle Perimeter",
            type: "concepts",
            description: "Perimeter of triangle PQR",
            value: "Sum of distances PQ + QR + PR",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "polar_coordinates",
            to: "angle_calculation",
            type: "enables",
            strength: 9
          },
          {
            from: "polar_coordinates",
            to: "collinearity_check",
            type: "enables",
            strength: 8
          },
          {
            from: "polar_coordinates",
            to: "triangle_perimeter",
            type: "enables",
            strength: 8
          }
        ]
      }
    }
  ],
  geometry: [
    {
      id: 'hkdse2024_geometry_01',
      problem: "圖1中，$E$ 為 $AC$ 與 $BD$ 的交點。已知 $\\angle ACB = \\angle ADB = 90°$ 及 $AD = BC$。Figure 1 description: A geometric figure showing two triangles ABC and ABD sharing side AB. Point E is the intersection of AC and BD. The figure shows that angles ACB and ADB are both 90 degrees, and AD equals BC. (a) 證明 $\\triangle ABC \\cong \\triangle BAD$。(b) 若 $AD = 12$ cm 及 $DE = 9$ cm，求五邊形 $ABCED$ 的面積。",
      problemType: "geometry_congruence",
      difficulty: "intermediate",
      description: "Working with congruent triangles and area",
      graphData: {
        nodes: [
          {
            id: "right_angles",
            name: "Right Angles",
            type: "concepts",
            description: "Both triangles have right angles",
            value: "∠ACB = ∠ADB = 90°",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "equal_sides",
            name: "Equal Sides",
            type: "concepts",
            description: "AD = BC",
            value: "AD = BC",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "congruent_triangles",
            name: "Congruent Triangles",
            type: "concepts",
            description: "△ABC ≅ △BAD",
            value: "Triangles are congruent",
            importance: 10,
            x: 0, y: 0, z: 0
          },
          {
            id: "pentagon_area",
            name: "Pentagon Area",
            type: "concepts",
            description: "Area of pentagon ABCED",
            value: "Calculate area using given dimensions",
            importance: 8,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          {
            from: "right_angles",
            to: "congruent_triangles",
            type: "proves",
            strength: 9
          },
          {
            from: "equal_sides",
            to: "congruent_triangles",
            type: "proves",
            strength: 9
          },
          {
            from: "congruent_triangles",
            to: "pentagon_area",
            type: "enables",
            strength: 8
          }
        ]
      }
    }
  ]
}; 